/**
 * Reminders slice for the application store.
 * Handles reminder storage and retrieval.
 */

import { logger } from '@/utils/logger';

import type { AgendaEvent } from '../types';
import type { StateCreator } from 'zustand';

/**
 * Reminders state and actions.
 */
export interface AgendaEventsSlice {
  // State
  reminders: AgendaEvent[];
  completedReminders: AgendaEvent[];
  isLoadingReminders: boolean;
  remindersError: string | null;
  /** The last active load hit the per-status cap, so some reminders may be missing. */
  remindersCapped: boolean;

  // Actions
  setReminders: (reminders: AgendaEvent[]) => void;
  setCompletedReminders: (reminders: AgendaEvent[]) => void;
  addReminder: (reminder: AgendaEvent) => void;
  updateReminder: (id: number, updates: Partial<AgendaEvent>) => void;
  removeReminder: (id: number) => void;
  setIsLoadingReminders: (loading: boolean) => void;
  setRemindersError: (error: string | null) => void;
  setRemindersCapped: (capped: boolean) => void;
  loadReminders: () => Promise<void>;
}

/**
 * Creates the reminders slice.
 */
export const createRemindersSlice: StateCreator<AgendaEventsSlice, [], [], AgendaEventsSlice> = (
  set,
  get
) => ({
  // Initial state
  reminders: [],
  completedReminders: [],
  isLoadingReminders: false,
  remindersError: null,
  remindersCapped: false,

  // Actions
  setReminders: reminders => set({ reminders }),

  setCompletedReminders: completedReminders => set({ completedReminders }),

  addReminder: reminder =>
    set(state => ({
      reminders: [...state.reminders, reminder],
    })),

  updateReminder: (id, updates) =>
    set(state => ({
      reminders: state.reminders.map(r => (r.id === id ? { ...r, ...updates } : r)),
      completedReminders: state.completedReminders.map(r =>
        r.id === id ? { ...r, ...updates } : r
      ),
    })),

  removeReminder: id =>
    set(state => ({
      reminders: state.reminders.filter(r => r.id !== id),
      completedReminders: state.completedReminders.filter(r => r.id !== id),
    })),

  setIsLoadingReminders: loading => set({ isLoadingReminders: loading }),

  setRemindersError: error => set({ remindersError: error }),

  setRemindersCapped: capped => set({ remindersCapped: capped }),

  loadReminders: async () => {
    const state = get();

    try {
      state.setIsLoadingReminders(true);
      state.setRemindersError(null);

      // Dynamic import to avoid circular dependencies
      const { remindersApi, activeListCapped, ACTIVE_REMINDERS_LIMIT } =
        await import('@features/agenda/services/remindersApi');
      const [active, completed] = await Promise.all([
        remindersApi.getReminders(undefined, ACTIVE_REMINDERS_LIMIT),
        remindersApi.getReminders('completed'),
      ]);

      set({
        reminders: active,
        completedReminders: completed,
        remindersCapped: activeListCapped(active),
      });
    } catch (error) {
      logger.error('Failed to load reminders:', error);
      set({ remindersError: 'Failed to load events' });
    } finally {
      set({ isLoadingReminders: false });
    }
  },
});
